import React, { useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Upload } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface CsvFileUploadProps {
  onDataLoaded: (data: any) => void;
}

export const CsvFileUpload = ({ onDataLoaded }: CsvFileUploadProps) => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const parseCSV = (text: string): any[] => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
    const rows: any[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
      const row: any = {};
      
      headers.forEach((header, index) => {
        const value = values[index];
        
        // Convert to appropriate types
        if (value === '' || value === 'null' || value === 'NULL') {
          row[header] = null;
        } else if (header.includes('price') || header.includes('amount') || header.includes('balance') || 
                   header.includes('payment') || header.includes('value') || header.includes('rate')) {
          row[header] = parseFloat(value) || null;
        } else if (header.includes('term') || header.includes('months') || header.includes('remaining')) {
          row[header] = parseInt(value) || null;
        } else if (header === 'has_balloon_payment' || header === 'buyer_current_on_contract') {
          row[header] = value.toLowerCase() === 'true' || value === '1';
        } else {
          row[header] = value;
        }
      });
      
      rows.push(row);
    }

    return rows;
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast({
        title: "Invalid file type",
        description: "Please upload a CSV file",
        variant: "destructive"
      });
      return;
    }

    try {
      const text = await file.text();
      const rows = parseCSV(text);
      
      if (rows.length === 0) {
        toast({
          title: "Empty file",
          description: "The CSV file contains no data",
          variant: "destructive"
        });
        return;
      }

      // Use first row for form auto-fill
      onDataLoaded(rows[0]);
      
      if (rows.length > 1) {
        toast({
          title: "Data loaded",
          description: `Loaded first row. File contains ${rows.length} total rows.`,
        });
      } else {
        toast({
          title: "Data loaded successfully",
          description: "Form has been pre-filled with contract data"
        });
      }
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error parsing CSV:', error);
      toast({
        title: "Error reading file",
        description: "Invalid CSV format",
        variant: "destructive"
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Quick Fill from CSV</CardTitle>
        <CardDescription>
          Upload a CSV file exported from Google Sheets with contract data
        </CardDescription>
      </CardHeader>
      <CardContent>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileUpload}
          className="hidden"
          id="csv-upload"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          className="w-full"
        >
          <Upload className="mr-2 h-4 w-4" />
          Upload CSV File
        </Button>
      </CardContent>
    </Card>
  );
};
